<template>
	<label :class="classes">
		<input v-model="model" :value type="checkbox" :disabled />
		<slot name="label" :value> {{ label }} </slot>
	</label>
</template>

<script setup lang="ts" generic="T">
export type ShCheckboxT<T> = {
	value?: T;
	label?: string;
	error?: string;
	disabled?: boolean;
	lg?: boolean;
	lg768?: boolean;
	lg1024?: boolean;
	lg1240?: boolean;
	lg1440?: boolean;
	lg920?: boolean;
	xl?: boolean;
	xl768?: boolean;
	xl1024?: boolean;
	xl1240?: boolean;
	xl1440?: boolean;
	xl920?: boolean;
};

const props = defineProps<ShCheckboxT<T>>();

const model = defineModel<T | T[]>({ required: true });

const classes = computed(() => {
	const sizeClass = getSizeClass();

	return {
		checkbox: true,
		disabled: props.disabled,
		...sizeClass
	};
});

function getSizeClass() {
	const classes: any = {};

	Object.entries(props)
		.filter((p) => !!p[1])
		.forEach(([key, value]) => {
			if (!key.startsWith('lg') && !key.startsWith('xl')) return;
			if (value === undefined || !value) return;

			if (typeof value === 'boolean') {
				return (classes[`size-${key}`] = value);
			}

			classes[`size-${key}${value}`] = true;
		});

	if (!Object.keys(classes).length) return { 'size-lg': true };

	return classes;
}
</script>

<style scoped lang="scss">
.checkbox {
	display: flex;
	flex-wrap: nowrap;
	align-items: center;
	gap: 0.667rem;
	color: var(--color-gray600);
	cursor: pointer;

	input {
		margin: 0;
		appearance: none;
		position: relative;
		-webkit-appearance: none;
		border-radius: 0.222rem;
		background-color: var(--color-gray50);
		border: 0.056rem solid var(--color-gray200);

		&::before {
			content: '';
			position: absolute;
			left: 0;
			right: 0;
			top: 0;
			bottom: 0;
			margin: auto;
			transform: scale(0);
			background-image: url('/images/svg/check.svg');
			background-position: center;
			background-repeat: no-repeat;
			background-size: contain;
			transition: all 0.1s;
		}

		&:hover {
			border-color: var(--color-gray300);
		}

		&:active,
		&:focus-visible {
			box-shadow:
				0rem 0rem 0rem 0.111rem var(--color-white),
				0rem 0rem 0rem 0.222rem var(--color-brand500);
		}

		&:checked:not(:disabled) {
			color: var(--color-white);
			border-color: var(--color-brand500);
			background-color: var(--color-brand500);
		}

		&:checked::before {
			transform: scale(1);
		}
	}
}

.checkbox.disabled,
.checkbox input:disabled {
	user-select: none;
	pointer-events: none;
}

.checkbox input:disabled {
	border-color: var(--color-gray100);
	background-color: var(--color-gray50);

	&::before {
		background-image: url('/images/svg/check-gray.svg');
	}
}

$sizes: (
	lg: tsms,
	xl: tmds
);
$screens: 768, 1024, 1240, 1440, 1920;

@mixin useSize($size, $font) {
	@include useFont($font);

	input {
		@include useFixedSize(
			var(--checkbox-#{$size}-size),
			var(--checkbox-#{$size}-size)
		);

		&::before {
			width: var(--checkbox-#{$size}-icon-s);
			height: var(--checkbox-#{$size}-icon-s);
		}
	}
}

@each $size, $font in $sizes {
	.size-#{$size} {
		@include useSize($size, $font);
	}
}

@each $screen in $screens {
	@each $size, $font in $sizes {
		.size-#{$size}#{$screen} {
			@media screen and (min-width: #{$screen}px) {
				@include useSize($size, $font);
			}
		}
	}
}
</style>
